import React, { useCallback, useRef, useState } from 'react';
import type { PiPublicPageData } from '../../patient-invisalign-public/types';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Pill } from '../Pill/Pill';
import {
  PatientUrgencyFooter,
  PatientUrgencyPracticeHeader,
} from '../PatientUrgency/PatientUrgencySections';
import { PiPublicArticleCard } from './PiPublicArticleCard';
import { PiPublicCard } from './PiPublicCard';
import { piPublicContainer, puDisplay, puRoot } from './piPublicClasses';

export interface PatientInvisalignPublicContentProps {
  data: PiPublicPageData;
}

function SmileTransformationSlider({
  before,
  after,
  className,
}: {
  before: string;
  after: string;
  className?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50);
  const [dragging, setDragging] = useState(false);

  const setPositionFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(0, Math.min(100, pct)));
  }, []);

  const startDrag = (clientX: number) => {
    setDragging(true);
    setPositionFromClientX(clientX);
  };

  return (
    <div
      ref={trackRef}
      className={cn(
        'relative h-[280px] w-full touch-none select-none overflow-hidden rounded-[var(--r-lg)] bg-[var(--cream-2)] md:h-[360px]',
        dragging ? 'cursor-grabbing' : 'cursor-grab',
        className,
      )}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        if ((e.target as HTMLElement).closest('[data-slider-control]')) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        startDrag(e.clientX);
      }}
      onPointerMove={(e) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        setPositionFromClientX(e.clientX);
      }}
      onPointerUp={(e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
        setDragging(false);
      }}
      onPointerCancel={() => setDragging(false)}
    >
      <div className="pointer-events-none absolute inset-0">
        <img src={after} alt="Goal" className="h-full w-full object-cover object-center" draggable={false} />
      </div>
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img src={before} alt="Before" className="h-full w-full object-cover object-center" draggable={false} />
      </div>

      {/* Divider + drag handle */}
      <div
        className="pointer-events-none absolute bottom-0 top-0 z-10 w-0.5 bg-[var(--cream-1)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--ink-0)_8%,transparent)]"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      />
      <div
        className="absolute top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${position}%` }}
        data-slider-control
      >
        <div className="inline-flex items-center justify-center gap-2 rounded-[var(--r-pill)] border border-[var(--success)] bg-[var(--cream-1)] px-3 py-2 shadow-[var(--sh-2)] md:gap-4 md:px-4">
          <button
            type="button"
            data-slider-control
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setPosition((p) => Math.max(0, p - 5));
            }}
            className="cursor-pointer border-0 bg-transparent p-0"
          >
            <Icon kind="arrow-l" size={16} />
          </button>
          <span className="hidden text-xs font-semibold text-[var(--ink-0)] md:inline">Slide</span>
          <button
            type="button"
            data-slider-control
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setPosition((p) => Math.min(100, p + 5));
            }}
            className="cursor-pointer border-0 bg-transparent p-0"
          >
            <Icon kind="arrow-r" size={16} />
          </button>
        </div>
      </div>

      <div
        className="pointer-events-none absolute bottom-4 left-0 right-0 z-30 flex items-center justify-between gap-2 px-2 md:px-4"
        data-slider-control
      >
        <Button
          variant="secondary"
          size="sm"
          className="pointer-events-auto"
          data-slider-control
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setPosition(0);
          }}
        >
          Before
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="pointer-events-auto"
          data-slider-control
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setPosition(100);
          }}
        >
          Goal
        </Button>
      </div>
    </div>
  );
}

export function PatientInvisalignPublicContent({ data }: PatientInvisalignPublicContentProps) {
  const { practice, profile, articles, visits, smileBefore, smileAfter, clincheckPoster } = data;
  const trayPct = Math.round((profile.currentTray / profile.totalTrays) * 100);

  return (
    <div className={puRoot}>
      <div className={piPublicContainer}>
        <PatientUrgencyPracticeHeader practice={practice} />

        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <PiPublicCard className="w-full items-start">
            <div className="flex flex-row items-start gap-3">
              <img
                src={profile.avatar}
                alt=""
                className="h-12 w-12 rounded-full object-cover md:h-[70px] md:w-[70px]"
              />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[var(--ink-2)] md:text-base">
                  Your Invisalign Profile
                </span>
                <h2 className={cn(puDisplay, 'm-0 text-lg font-semibold text-[var(--ink-0)] md:text-2xl')}>
                  {profile.name}
                </h2>
              </div>
            </div>
            <div className="mt-2 flex w-full justify-center md:mt-0">
              <Pill tone="success">
                <span className="mr-1.5 inline-block h-2 w-2 animate-pulse rounded-full bg-current" />
                {profile.statusLabel}
              </Pill>
            </div>
          </PiPublicCard>

          <PiPublicCard className="w-full items-start justify-between">
            <div>
              <span className="text-sm text-[var(--ink-2)] md:text-base">Next Appointment</span>
              <h2 className={cn(puDisplay, 'm-0 text-lg font-semibold text-[var(--ink-0)] md:text-xl')}>
                {profile.nextAppointment}
              </h2>
              <span className="text-xs text-[var(--success)] md:text-sm">{profile.nextAppointmentDetail}</span>
            </div>
            <div className="mt-4 flex w-full flex-col items-start justify-between gap-3 md:mt-0 md:flex-row md:items-center">
              <span className="text-xs text-[var(--ink-2)] md:text-sm">{profile.provider}</span>
              <Button variant="primary" size="sm" iconLeft="calendar" className="w-full md:w-auto">
                Reschedule
              </Button>
            </div>
          </PiPublicCard>

          <PiPublicCard className="w-full items-start justify-between">
            <span className="text-base text-[var(--ink-1)]">Current Tray</span>
            <div className="flex w-full flex-row items-start justify-between">
              <div className="flex items-baseline gap-1 self-end">
                <span className="text-lg font-semibold text-[var(--ink-0)] md:text-xl">{profile.currentTray}</span>
                <span className="text-xs text-[var(--ink-2)] md:text-sm">/ {profile.totalTrays}</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs text-[var(--ink-2)] md:text-sm">Days left</span>
                <span className="text-lg font-semibold text-[var(--success)] md:text-xl">
                  {profile.daysLeft} days
                </span>
              </div>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--cream-2)]">
              <div
                className="h-full rounded-full bg-[var(--success)] transition-all duration-300"
                style={{ width: `${trayPct}%` }}
              />
            </div>
            <div className="flex w-full items-center gap-2">
              <Icon kind="refresh" size={18} style={{ color: 'var(--success)' }} />
              <span className="text-xs text-[var(--ink-2)] md:text-sm">
                Switch to tray {profile.currentTray + 1} on{' '}
                <strong className="text-[var(--ink-0)]">{profile.switchTrayDate}</strong>
              </span>
            </div>
          </PiPublicCard>
        </div>

        <div className="mt-4 grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <PiPublicCard className="w-full items-start gap-4">
            <span className="text-sm font-semibold text-[var(--ink-0)] md:text-base">Clincheck Simulation</span>
            <div className="w-full overflow-hidden rounded-[var(--r-lg)] bg-[var(--cream-0)]">
              <div className="flex min-h-[250px] items-center justify-center md:min-h-[420px]">
                <img src={clincheckPoster} alt="" className="max-h-[420px] w-full object-contain opacity-80" />
              </div>
            </div>
          </PiPublicCard>

          <PiPublicCard className="w-full items-stretch gap-4">
            <span className="text-sm font-semibold text-[var(--ink-0)] md:text-base">Smile Transformation</span>
            <SmileTransformationSlider before={smileBefore} after={smileAfter} />
          </PiPublicCard>
        </div>

        <PiPublicCard className="mt-4 gap-4">
          <div className="flex w-full flex-col justify-between gap-3 md:flex-row md:items-center">
            <span className="text-sm font-semibold text-[var(--ink-0)] md:text-base">Financial Overview</span>
            <Pill tone="success">Total Cost: {profile.totalCost}</Pill>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="flex flex-col items-center gap-4 md:flex-row md:items-center">
              <div className="relative h-24 w-24 shrink-0 md:h-32 md:w-32">
                <svg className="h-24 w-24 -rotate-90 transform md:h-32 md:w-32" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="var(--cream-2)" strokeWidth="10" />
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="var(--ai-4)"
                    strokeWidth="10"
                    strokeDasharray={`${2 * Math.PI * 50}`}
                    strokeDashoffset={`${2 * Math.PI * 50 * (1 - profile.progressPct / 100)}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xs text-[var(--ink-3)]">Remaining</span>
                  <span className="text-lg font-bold text-[var(--ink-0)] md:text-xl">{profile.remainingBalance}</span>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {[
                  { label: 'Insurance Paid', value: profile.insurancePaid, dot: 'var(--ai-4)' },
                  { label: 'Patient Paid', value: profile.patientPaid, dot: 'var(--info)' },
                  { label: 'Balance Due', value: profile.balanceDue, dot: 'var(--ai-4)' },
                ].map((row) => (
                  <div key={row.label} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: row.dot }} />
                    <span className="text-sm text-[var(--ink-1)]">{row.label}</span>
                    <span className="ml-auto text-sm font-semibold text-[var(--ink-0)]">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <h3 className="m-0 text-base font-normal text-[var(--ink-1)]">Quick actions</h3>
              <div className="flex cursor-pointer items-center gap-3 rounded-[var(--r-pill)] bg-[color-mix(in_srgb,var(--ai-4)_10%,var(--cream-1))] p-3 md:p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--ai-4)_18%,transparent)]">
                  <Icon kind="shield" size={18} style={{ color: 'var(--ai-4)' }} />
                </span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[var(--ink-0)] md:text-base">Insurance claim status</div>
                  <div className="text-xs font-semibold text-[var(--ai-4)] md:text-sm">{profile.pendingClaims}</div>
                </div>
                <Icon kind="arrow-r" size={18} style={{ color: 'var(--ink-3)' }} />
              </div>
              <div className="rounded-[var(--r-pill)] border border-[color-mix(in_srgb,var(--ai-4)_18%,var(--cream-2))] bg-[var(--cream-1)] px-4 py-3 md:px-8 md:py-4">
                <div className="text-sm font-semibold text-[var(--ink-0)] md:text-base">
                  Monthly payment plan: Active
                </div>
                <div className="text-xs text-[var(--ink-2)]">{profile.paymentPlanNote}</div>
              </div>
            </div>
          </div>
        </PiPublicCard>

        <PiPublicCard className="mt-4 gap-4">
          <div className="flex w-full flex-col items-start justify-between gap-3 md:flex-row md:items-center">
            <span className="text-sm font-semibold text-[var(--ink-0)] md:text-base">
              Success Guide & Resources section
            </span>
            <Button variant="ghost" size="sm">
              View all articles <Icon kind="arrow-r" size={14} />
            </Button>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {articles.map((article) => (
              <PiPublicArticleCard key={article.id} article={article} />
            ))}
          </div>
        </PiPublicCard>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <PiPublicCard className="items-start gap-4">
            <div className="flex flex-row items-center gap-2">
              <img src="/invisalign/teeth.png" alt="" className="h-10 w-10 md:h-12 md:w-12" />
              <Pill tone="success">PHASE 2: RETENTION</Pill>
            </div>
            <h2 className={cn(puDisplay, 'm-0 mt-2 text-lg font-semibold text-[var(--ink-0)] md:text-xl')}>
              Protect Your Investment with Vivera™
            </h2>
            <p className="m-0 text-xs text-[var(--ink-2)] md:text-sm">
              Don&apos;t let your teeth shift back. Vivera retainers are 30% stronger than other leading retainers.
            </p>
            <div className="mt-6 w-full space-y-3">
              <div className="flex items-center justify-between border-b border-[var(--cream-2)] pb-1.5">
                <span className="text-sm text-[var(--ink-1)]">Cost of Retreatment</span>
                <span className="text-base font-semibold text-[var(--terracotta)] line-through">$3,500+</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[var(--ink-0)]">4 Sets of Vivera</span>
                <span className="text-sm font-semibold text-[var(--success)]">$600</span>
              </div>
              <Button variant="primary" size="md" className="mt-4 w-full">
                Add Vivera to Plan
              </Button>
            </div>
          </PiPublicCard>

          <PiPublicCard className="gap-4">
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-semibold text-[var(--ink-0)] md:text-base">Visit History</span>
              <button type="button" className="border-0 bg-transparent text-xs font-medium text-[var(--success)] md:text-sm">
                View all
              </button>
            </div>
            <div className="flex w-full flex-col gap-3">
              {visits.map((visit) => (
                <div
                  key={visit.id}
                  className="flex items-center gap-4 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 md:gap-6 md:p-5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--success-tint)]">
                    <Icon kind="check-circ" size={18} style={{ color: 'var(--success)' }} />
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[var(--ink-0)] md:text-base">{visit.title}</div>
                    <div className="text-xs text-[var(--ink-2)]">{visit.date}</div>
                  </div>
                  <Icon kind="down" size={18} style={{ color: 'var(--ink-2)' }} />
                </div>
              ))}
            </div>
          </PiPublicCard>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-6 rounded-[var(--r-xl)] bg-[color-mix(in_srgb,var(--info)_12%,var(--cream-1))] px-4 py-6 md:rounded-[var(--r-xl)] md:px-5 md:py-8 lg:grid-cols-2">
          <div>
            <span className="text-sm font-semibold text-[var(--ink-1)] md:text-base">
              Get the full experience on mobile
            </span>
            <p className={cn(puDisplay, 'my-4 text-lg font-semibold text-[var(--ink-0)] md:my-5 md:text-xl')}>
              Making it easy for you in the My Invisalign mobile app.
            </p>
            <h3 className="mb-4 text-sm font-normal text-[var(--ink-1)]">With the My Invisalign app you can:</h3>
            <ul className="mb-6 list-inside list-disc space-y-0 pl-2 text-sm text-[var(--ink-1)]">
              <li>See which tray you&apos;re on and when to switch to the next one</li>
              <li>Get reminders so you don&apos;t forget wear time or change days</li>
              <li>Track your progress with photos and milestones</li>
              <li>Access tips for caring for your aligners and keeping your smile on track</li>
            </ul>
            <p className="text-sm text-[var(--ink-2)]">
              Take a moment at your first appointment to scan the QR code and set up the app—you&apos;ll have your
              entire Invisalign journey organized in your pocket from day one.
            </p>
            <div className="mt-4 flex flex-row items-start gap-2">
              <img src="/invisalign/google.png" alt="Google Play" className="h-[38px] w-[120px] md:h-12 md:w-[153px]" />
              <img src="/invisalign/apple.png" alt="App Store" className="h-[38px] w-[120px] md:h-12 md:w-[153px]" />
            </div>
          </div>
          <div className="mt-4 flex items-end justify-center lg:mt-0">
            <img src="/invisalign/mobile.png" alt="" className="h-[305px] w-[305px] object-contain" />
          </div>
        </div>

        <PatientUrgencyFooter practice={practice} />
      </div>
    </div>
  );
}
